import {Component, EventEmitter, Input, OnChanges, Output} from '@angular/core';
import {NgFor, NgIf} from '@angular/common';
import {
  MeetPhoneInviteService, PhoneCountry, PhoneInvite
} from '../../../../startup-modules/meet.orbitax.com/services/meet/meet-phone-invite.service';

/** How many dialogs were made: each one's field ids are its own. */
let dialogs = 0;

/**
 * Add by phone (ITP-E-198-01): the small dialog that dials a phone person into a call — the in-app call's ⋯ today, Meet's
 * participants panel next (D-08), so both ask the same three things the same way:
 *
 * - **Country:** only the countries the account dials (`getSupportCountryInfo()`, passed in), with a search, the user's own
 *   first where it is listed ({@link MeetPhoneInviteService.defaultCountry}).
 * - **Number:** digits only — spaces, dashes, dots and brackets stripped — 4 to 15 of them.
 * - **Name:** how they show in the call; required, 60 characters at most.
 *
 * **Call** emits the invite, the number as digits; the host dials it and shows where it stands in the participants list,
 * where Cancel is while it rings. Presentational: it never dials and never reads the SDK.
 */
@Component({
  selector: 'app-phone-invite-dialog',
  standalone: true,
  imports: [NgIf, NgFor],
  templateUrl: './phone-invite-dialog.component.html',
  styleUrls: ['./phone-invite-dialog.component.scss']
})
export class PhoneInviteDialogComponent implements OnChanges {
  readonly maxName = MeetPhoneInviteService.maxName;
  /** Its fields' ids, its own: a call's dialog and Meet's never share one. */
  readonly uid = `phone-invite-${++dialogs}`;

  /** The countries the account dials. */
  @Input({required: true}) countries: PhoneCountry[] = [];
  /** Whose region picks the first country; the browser's unless a host says otherwise. */
  @Input() locale: string = navigator.language;

  @Output() readonly call = new EventEmitter<PhoneInvite>();
  @Output() readonly close = new EventEmitter<void>();

  search = '';
  country: PhoneCountry | null = null;
  number = '';
  name = '';

  ngOnChanges(): void {
    if (!this.country || !this.countries.includes(this.country)) {
      this.country = MeetPhoneInviteService.defaultCountry(this.countries, this.locale);
    }
  }

  /** The countries the search matches, by name or code; the one chosen always stays in the list. */
  get shown(): PhoneCountry[] {
    const search = this.search.trim().toLowerCase();
    if (!search) {
      return this.countries;
    }
    return this.countries.filter(country => country === this.country
      || country.name.toLowerCase().includes(search) || country.code.replace(/\s/g, '').includes(search.replace(/\s/g, '')));
  }

  /** The number as digits, or null while it is not 4 to 15 of them. */
  get digits(): string | null {
    return MeetPhoneInviteService.digits(this.number);
  }

  /** Something was typed that is not a number to dial. */
  get numberInvalid(): boolean {
    return this.number.trim() !== '' && !this.digits;
  }

  get canCall(): boolean {
    return !!this.country && !!this.digits && !!MeetPhoneInviteService.nameOf(this.name);
  }

  keyOf(country: PhoneCountry): string {
    return `${country.id || country.name}|${country.code}`;
  }

  onSearch(value: string): void {
    this.search = value ?? '';
  }

  onCountry(key: string): void {
    this.country = this.countries.find(country => this.keyOf(country) === key) ?? this.country;
  }

  onNumber(value: string): void {
    this.number = value ?? '';
  }

  onName(value: string): void {
    this.name = value ?? '';
  }

  onCall(): void {
    const number = this.digits;
    const name = MeetPhoneInviteService.nameOf(this.name);
    if (!this.country || !number || !name) {
      return;
    }
    this.call.emit({countryCode: this.country.code, number, name});
  }

  onClose(): void {
    this.close.emit();
  }

  trackCountry = (_: number, country: PhoneCountry): string => this.keyOf(country);
}
